import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useProjectsContext } from 'src/modules/projects/context';

import { ApiError } from '../../../common/http/mapToErrors';
import { TierFormValues } from '../domain/types';
import { tiersService } from '../infra/tiersService';
import { tiersQueryKeys } from './useTiersQuery';

interface UseUpdateTierMutationOptions {
  onSuccess?: () => void;
}

export function useUpdateTierMutation(tierId: string, options?: UseUpdateTierMutationOptions) {
  const { project } = useProjectsContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: TierFormValues) => {
      const trimmedDescription = values.description.trim();
      return tiersService.update(project?.id as string, tierId, {
        name: values.name,
        rank: values.rank,
        description: trimmedDescription ? trimmedDescription : null,
        audience_id: values.audienceId || null,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: tiersQueryKeys.all(project?.id ?? '') });
      toast.success('Tier updated successfully');
      options?.onSuccess?.();
    },
    onError: (error: ApiError) => {
      if (error.statusCode === 409) {
        toast.error('A tier with this name, rank, or audience already exists.');
      } else if (error.statusCode === 403) {
        toast.error('Admin role required.');
      } else {
        toast.error(error.message || 'Failed to update tier');
      }
    },
  });
}
